import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { euro, dateFR } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";

export const FAB_STATUSES = [
  { key: "commandee", label: "Commandée", cls: "bg-slate-100 text-slate-700" },
  { key: "en_fabrication", label: "En fabrication", cls: "bg-amber-100 text-amber-800" },
  { key: "prete", label: "Prête", cls: "bg-emerald-100 text-emerald-800" },
  { key: "livree", label: "Livrée", cls: "bg-[#9E7B41]/15 text-[#7a5e2f]" },
];
const KINDS = { stock: "Commande stock", demo: "Véhicule de démo" };
const who = (c = {}) => [c.company, [c.firstname, c.name].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "—";
const sel = "h-8 rounded-md border border-input bg-background px-2 text-sm";

const StatusSelect = ({ order, onChange }) => {
  const st = FAB_STATUSES.find((s) => s.key === order.fab_status) || FAB_STATUSES[0];
  return (
    <select value={st.key} onChange={(e) => onChange(order, e.target.value)} className={`${sel} font-medium ${st.cls}`} data-testid={`fab-status-${order.quote_number}`}>
      {FAB_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
    </select>
  );
};

export const StockOrdersTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("open");

  useEffect(() => {
    api.get("/admin/stock-orders").then(({ data }) => setList(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const change = async (o, v) => {
    const prev = o.fab_status;
    setList((xs) => xs.map((x) => (x.id === o.id ? { ...x, fab_status: v } : x)));
    try {
      await api.put(`/admin/stock-orders/${o.id}/status`, { fab_status: v });
      toast.success(`${o.quote_number} : ${FAB_STATUSES.find((s) => s.key === v).label}`);
    } catch (e) {
      setList((xs) => xs.map((x) => (x.id === o.id ? { ...x, fab_status: prev } : x)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return list.filter((o) => (kind === "all" || o.stock_order === kind)
      && (status === "all" || (status === "open" ? o.fab_status !== "livree" : o.fab_status === status))
      && (!term || [o.quote_number, o.model_name, who(o.client), o.delivery_place].some((v) => (v || "").toLowerCase().includes(term))));
  }, [list, q, kind, status]);
  const count = (k) => list.filter((o) => o.fab_status === k).length;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return (
    <div className="space-y-4" data-testid="stock-orders-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2.5 top-2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (n°, revendeur, modèle, lieu)" className="pl-8 h-8" data-testid="stock-orders-search" />
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={sel} data-testid="stock-orders-kind">
          <option value="all">Stock et démos</option><option value="stock">Commandes stock</option><option value="demo">Véhicules de démo</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel} data-testid="stock-orders-status-filter">
          <option value="open">Non livrées ({list.length - count("livree")})</option>
          <option value="all">Tous les statuts ({list.length})</option>
          {FAB_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label} ({count(s.key)})</option>)}
        </select>
      </div>
      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>N°</TableHead><TableHead>Signée le</TableHead><TableHead>Type</TableHead><TableHead>Revendeur</TableHead>
            <TableHead>Véhicule</TableHead><TableHead>Lieu de livraison</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut de fabrication</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((o) => (
              <TableRow key={o.id} data-testid={`stock-order-row-${o.quote_number}`}>
                <TableCell className="font-mono text-sm whitespace-nowrap">{o.quote_number}</TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{dateFR(o.signed_at)}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] border-primary text-primary whitespace-nowrap">{KINDS[o.stock_order]}</Badge></TableCell>
                <TableCell className="text-sm">{who(o.client)}</TableCell>
                <TableCell className="text-sm">{o.model_name}{o.color && <div className="text-[11px] text-muted-foreground">{o.color}</div>}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{o.delivery_place || "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm whitespace-nowrap">{euro(o.total_public_ttc)} TTC</TableCell>
                <TableCell><StatusSelect order={o} onChange={change} />{o.fab_status_at && <div className="text-[10px] text-muted-foreground mt-0.5">le {dateFR(o.fab_status_at)}</div>}</TableCell>
              </TableRow>
            ))}
            {!shown.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10" data-testid="stock-orders-empty">Aucune commande stock ou démo signée</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
